import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Check, Eye, EyeOff, Headphones, Repeat, BookOpen, ListChecks } from "lucide-react";
import api from "@/config/api";
import { useAuth } from "@/contexts/AuthContext";
import { AudioPlayer } from "@/components/AudioPlayer";
import { SurahReader } from "@/components/SurahReader";
import { GamifiedButton } from "@/components/GamifiedButton";
import { Loading } from "@/components/Feedback";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSeo } from "@/hooks/useSeo";
import { cn } from "@/lib/utils";

const MODES = [
  { id: "listen", label: "Écouter", icon: Headphones },
  { id: "follow", label: "Écouter + suivre", icon: Eye },
  { id: "repeat", label: "Répéter", icon: Repeat },
  { id: "memorize", label: "Sans regarder", icon: EyeOff },
];

export default function Learn() {
  useSeo("Apprendre les sourates", "Mémorise le Coran verset par verset ou en lecture complète : texte arabe, traduction française, audio Al-Afasy et répétitions guidées.");
  const { refresh } = useAuth();
  const qc = useQueryClient();
  const [surahNum, setSurahNum] = useState(1);
  const [ayahIdx, setAyahIdx] = useState(0);
  const [mode, setMode] = useState("follow");
  const [targetReps, setTargetReps] = useState(5);
  const [reps, setReps] = useState(0);
  const [view, setView] = useState("verse"); // verse | full

  const { data: surahs } = useQuery({ queryKey: ["surahs"], queryFn: async () => (await api.get("/surahs")).data });
  const { data: surah, isLoading } = useQuery({
    queryKey: ["surah", surahNum],
    queryFn: async () => (await api.get(`/surahs/${surahNum}`)).data,
  });

  useEffect(() => { setAyahIdx(0); setReps(0); }, [surahNum]);
  useEffect(() => { setReps(0); }, [ayahIdx]);
  const ayahs = surah?.ayahs || [];
  const ayah = ayahs[ayahIdx];
  const hideText = mode === "memorize";
  const showTranslation = mode !== "listen" && mode !== "memorize";

  const learn = useMutation({
    mutationFn: async () => (await api.post("/learn", { surah_number: surahNum, ayah_number: ayah.number_in_surah })).data,
    onSuccess: async () => {
      toast.success("Verset ajouté à ta mémorisation ! +15 points ⭐");
      await refresh();
      qc.invalidateQueries({ queryKey: ["progress-summary"] });
      qc.invalidateQueries({ queryKey: ["reviews-due"] });
    },
    onError: () => toast.error("Erreur lors de l'enregistrement."),
  });

  const finishReading = useMutation({
    mutationFn: async () => (await api.post("/sessions", { type: "lecture_complete", duration_minutes: 5 })).data,
    onSuccess: async (d) => {
      toast.success(`Lecture complète terminée ! +${d.points_earned} points 🎉`);
      await refresh();
      qc.invalidateQueries();
    },
    onError: () => toast.error("Erreur."),
  });

  const surahList = useMemo(() => surahs || [], [surahs]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-black text-slate-800">Apprendre</h1>
        <p className="text-slate-500">Choisis une sourate et apprends verset par verset, ou lis-la en entier.</p>
      </div>

      {/* View toggle */}
      <div className="inline-flex bg-slate-100 rounded-2xl p-1" data-testid="learn-view-toggle">
        <button data-testid="view-verse" onClick={() => setView("verse")}
          className={cn("flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-colors",
            view === "verse" ? "bg-white text-primary shadow-sm" : "text-slate-500")}>
          <ListChecks className="w-4 h-4" /> Par verset
        </button>
        <button data-testid="view-full" onClick={() => setView("full")}
          className={cn("flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-colors",
            view === "full" ? "bg-white text-primary shadow-sm" : "text-slate-500")}>
          <BookOpen className="w-4 h-4" /> Sourate complète
        </button>
      </div>

      {/* Selectors */}
      <div className="bg-white p-5 rounded-3xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2 block">Sourate</label>
            <Select value={String(surahNum)} onValueChange={(v) => setSurahNum(Number(v))}>
              <SelectTrigger data-testid="surah-select" className="w-full px-4 py-6 rounded-2xl border-2 border-slate-200 font-bold text-base">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                {surahList.map((s) => (
                  <SelectItem key={s.number} value={String(s.number)} className="font-semibold">{s.number}. {s.name_fr}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2 block">Répétitions cibles</label>
            <div className="flex gap-2">
              {[3, 5, 10].map((r) => (
                <button key={r} data-testid={`reps-${r}`} onClick={() => { setTargetReps(r); setReps(0); }}
                  className={cn("flex-1 py-3 rounded-2xl font-bold border-2 transition-colors",
                    targetReps === r ? "border-secondary bg-secondary/10 text-secondary" : "border-slate-200 text-slate-500")}>
                  {r}×
                </button>
              ))}
            </div>
          </div>
        </div>
        {view === "verse" && (
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2 block">Mode d'apprentissage</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {MODES.map((m) => (
              <button key={m.id} data-testid={`mode-${m.id}`} onClick={() => setMode(m.id)}
                className={cn("flex items-center justify-center gap-2 py-2.5 rounded-2xl font-bold text-sm border-2 transition-colors",
                  mode === m.id ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-slate-500")}>
                <m.icon className="w-4 h-4" /> {m.label}
              </button>
            ))}
          </div>
        </div>
        )}
      </div>

      {view === "full" ? (
        isLoading || !surah ? <Loading /> : (
          <motion.div key={`full-${surahNum}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-black text-slate-800">{surah.name_fr}</h2>
                <p className="font-arabic text-2xl text-slate-700">{surah.name_ar}</p>
              </div>
              <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-extrabold">{ayahs.length} versets</span>
            </div>
            <SurahReader surah={surah} />
            <GamifiedButton className="w-full mt-6" onClick={() => finishReading.mutate()} disabled={finishReading.isPending} data-testid="finish-reading-btn">
              <Check className="w-5 h-5" /> J'ai terminé la lecture complète
            </GamifiedButton>
          </motion.div>
        )
      ) : isLoading || !ayah ? <Loading /> : (
        <motion.div key={`${surahNum}-${ayahIdx}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
          <div className="flex items-center justify-between mb-6">
            <span className="text-sm font-bold text-slate-400">{surah.name_fr}</span>
            <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-extrabold">
              Verset {ayah.number_in_surah} / {ayahs.length}
            </span>
          </div>

          {hideText ? (
            <div className="py-10 text-center text-slate-300 font-arabic text-4xl">••• ••• •••</div>
          ) : (
            <p className="font-arabic text-4xl sm:text-5xl leading-[2.4] text-slate-900 text-right mb-6" data-testid="learn-arabic">
              {ayah.text_ar}
            </p>
          )}

          {showTranslation && (
            <p className="text-slate-600 leading-relaxed border-t border-slate-100 pt-4 mb-6" data-testid="learn-translation">
              {ayah.translation_fr}
            </p>
          )}

          <AudioPlayer src={ayah.audio_url} className="mb-6" onEnded={() => setReps((r) => Math.min(targetReps, r + 1))} />

          {/* Repetition counter */}
          <div className="mb-6">
            <div className="flex items-center justify-between text-sm font-bold text-slate-500 mb-2">
              <span>Répétition {reps}/{targetReps}</span>
              <button onClick={() => setReps((r) => Math.min(targetReps, r + 1))} data-testid="add-rep" className="text-primary hover:underline">+ compter une répétition</button>
            </div>
            <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-primary to-secondary rounded-full transition-[width]" style={{ width: `${(reps / targetReps) * 100}%` }} />
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <button data-testid="prev-ayah" disabled={ayahIdx === 0} onClick={() => setAyahIdx((i) => Math.max(0, i - 1))}
              className="w-12 h-12 rounded-2xl border-2 border-slate-200 flex items-center justify-center text-slate-500 disabled:opacity-40 hover:bg-slate-50">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <GamifiedButton variant="secondary" onClick={() => learn.mutate()} disabled={learn.isPending} className="flex-1" data-testid="learn-mark-btn">
              <Check className="w-5 h-5" /> J'ai mémorisé ce verset
            </GamifiedButton>
            <button data-testid="next-ayah" disabled={ayahIdx >= ayahs.length - 1} onClick={() => setAyahIdx((i) => Math.min(ayahs.length - 1, i + 1))}
              className="w-12 h-12 rounded-2xl border-2 border-slate-200 flex items-center justify-center text-slate-500 disabled:opacity-40 hover:bg-slate-50">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
